package link.erpango.quip.db

import app.cash.sqldelight.ExecutableQuery
import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Double
import kotlin.Long
import kotlin.String

public class SparePartDocumentQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> selectAll(mapper: (
    id: Long,
    server_id: Long?,
    spare_part_id: Long,
    server_spare_part_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = Query(641_476_277, arrayOf("SparePartDocument"), driver, "SparePartDocument.sq", "selectAll", """
  |SELECT SparePartDocument.id, SparePartDocument.server_id, SparePartDocument.spare_part_id, SparePartDocument.server_spare_part_id, SparePartDocument.vehicle_vin, SparePartDocument.name, SparePartDocument.file_path, SparePartDocument.file_type, SparePartDocument.created_at, SparePartDocument.size_mb, SparePartDocument.updated_at
  |FROM SparePartDocument
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getLong(2)!!,
      cursor.getLong(3),
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getDouble(9)!!,
      cursor.getString(10)
    )
  }

  public fun selectAll(): Query<SparePartDocument> = selectAll(::SparePartDocument)

  public fun <T : Any> selectBySparePartId(spare_part_id: Long, mapper: (
    id: Long,
    server_id: Long?,
    spare_part_id: Long,
    server_spare_part_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = SelectBySparePartIdQuery(spare_part_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getLong(2)!!,
      cursor.getLong(3),
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getDouble(9)!!,
      cursor.getString(10)
    )
  }

  public fun selectBySparePartId(spare_part_id: Long): Query<SparePartDocument> = selectBySparePartId(spare_part_id, ::SparePartDocument)

  public fun <T : Any> selectByServerId(server_id: Long?, mapper: (
    id: Long,
    server_id: Long?,
    spare_part_id: Long,
    server_spare_part_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = SelectByServerIdQuery(server_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getLong(2)!!,
      cursor.getLong(3),
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getDouble(9)!!,
      cursor.getString(10)
    )
  }

  public fun selectByServerId(server_id: Long?): Query<SparePartDocument> = selectByServerId(server_id, ::SparePartDocument)

  public fun <T : Any> selectById(id: Long, mapper: (
    id: Long,
    server_id: Long?,
    spare_part_id: Long,
    server_spare_part_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = SelectByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getLong(2)!!,
      cursor.getLong(3),
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getString(7)!!,
      cursor.getString(8)!!,
      cursor.getDouble(9)!!,
      cursor.getString(10)
    )
  }

  public fun selectById(id: Long): Query<SparePartDocument> = selectById(id, ::SparePartDocument)

  public fun last_insert_rowid(): ExecutableQuery<Long> = Query(-1_107_098_072, driver, "SparePartDocument.sq", "last_insert_rowid", "SELECT last_insert_rowid()") { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertDocument(
    id: Long?,
    server_id: Long?,
    spare_part_id: Long,
    server_spare_part_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ): Long {
    val result = driver.execute(1_342_288_196, """
        |INSERT OR REPLACE INTO SparePartDocument(id, server_id, spare_part_id, server_spare_part_id, vehicle_vin, name, file_path, file_type, created_at, size_mb, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 11) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindLong(parameterIndex++, server_id)
          bindLong(parameterIndex++, spare_part_id)
          bindLong(parameterIndex++, server_spare_part_id)
          bindString(parameterIndex++, vehicle_vin)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, file_path)
          bindString(parameterIndex++, file_type)
          bindString(parameterIndex++, created_at)
          bindDouble(parameterIndex++, size_mb)
          bindString(parameterIndex++, updated_at)
        }.await()
    notifyQueries(1_342_288_196) { emit ->
      emit("SparePartDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insert(
    server_id: Long?,
    spare_part_id: Long,
    server_spare_part_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ): Long {
    val result = driver.execute(-993_553_143, """
        |INSERT INTO SparePartDocument(server_id, spare_part_id, server_spare_part_id, vehicle_vin, name, file_path, file_type, created_at, size_mb, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 10) {
          var parameterIndex = 0
          bindLong(parameterIndex++, server_id)
          bindLong(parameterIndex++, spare_part_id)
          bindLong(parameterIndex++, server_spare_part_id)
          bindString(parameterIndex++, vehicle_vin)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, file_path)
          bindString(parameterIndex++, file_type)
          bindString(parameterIndex++, created_at)
          bindDouble(parameterIndex++, size_mb)
          bindString(parameterIndex++, updated_at)
        }.await()
    notifyQueries(-993_553_143) { emit ->
      emit("SparePartDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteById(id: Long): Long {
    val result = driver.execute(1_831_514_733, """
        |DELETE FROM SparePartDocument
        |WHERE id = ?
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
        }.await()
    notifyQueries(1_831_514_733) { emit ->
      emit("SparePartDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteAllBySparePartId(spare_part_id: Long): Long {
    val result = driver.execute(252_122_952, """
        |DELETE FROM SparePartDocument
        |WHERE spare_part_id = ?
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, spare_part_id)
        }.await()
    notifyQueries(252_122_952) { emit ->
      emit("SparePartDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteAll(): Long {
    val result = driver.execute(1_998_742_438, """DELETE FROM SparePartDocument""", 0).await()
    notifyQueries(1_998_742_438) { emit ->
      emit("SparePartDocument")
    }
    return result
  }

  private inner class SelectBySparePartIdQuery<out T : Any>(
    public val spare_part_id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("SparePartDocument", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("SparePartDocument", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(1_222_971_746, """
    |SELECT SparePartDocument.id, SparePartDocument.server_id, SparePartDocument.spare_part_id, SparePartDocument.server_spare_part_id, SparePartDocument.vehicle_vin, SparePartDocument.name, SparePartDocument.file_path, SparePartDocument.file_type, SparePartDocument.created_at, SparePartDocument.size_mb, SparePartDocument.updated_at
    |FROM SparePartDocument
    |WHERE spare_part_id = ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, spare_part_id)
    }

    override fun toString(): String = "SparePartDocument.sq:selectBySparePartId"
  }

  private inner class SelectByServerIdQuery<out T : Any>(
    public val server_id: Long?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("SparePartDocument", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("SparePartDocument", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(null, """
    |SELECT SparePartDocument.id, SparePartDocument.server_id, SparePartDocument.spare_part_id, SparePartDocument.server_spare_part_id, SparePartDocument.vehicle_vin, SparePartDocument.name, SparePartDocument.file_path, SparePartDocument.file_type, SparePartDocument.created_at, SparePartDocument.size_mb, SparePartDocument.updated_at
    |FROM SparePartDocument
    |WHERE server_id ${ if (server_id == null) "IS" else "=" } ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, server_id)
    }

    override fun toString(): String = "SparePartDocument.sq:selectByServerId"
  }

  private inner class SelectByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("SparePartDocument", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("SparePartDocument", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_589_030_594, """
    |SELECT SparePartDocument.id, SparePartDocument.server_id, SparePartDocument.spare_part_id, SparePartDocument.server_spare_part_id, SparePartDocument.vehicle_vin, SparePartDocument.name, SparePartDocument.file_path, SparePartDocument.file_type, SparePartDocument.created_at, SparePartDocument.size_mb, SparePartDocument.updated_at
    |FROM SparePartDocument
    |WHERE id = ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, id)
    }

    override fun toString(): String = "SparePartDocument.sq:selectById"
  }
}
